import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import downloadGif from "assets/gif/download.gif";
import SuccessGif from "assets/gif/success.gif";
import ErrorGif from "assets/gif/error.gif";
import CheckIcon from "assets/icons/CheckIcon";
import XIcon from "assets/icons/XIcon";

import styles from "./style.module.scss";
import VLButton, { VLButtonProps } from "../../uikit/Buttons/VLButton";

export interface VLToastComponentProps {
  status: "IN_PROGRESS" | "SUCCESS" | "ERROR";
  title: string;
  onClose?: () => void;
  helperText?: string | JSX.Element;
  description: string;
  progress?: number;
  icon?: any;
  actionButton?: VLButtonProps;
}

const VLToastComponent = ({
  icon,
  status,
  title,
  onClose,
  progress,
  helperText,
  description,
  actionButton,
}: VLToastComponentProps) => {
  const toastRef = useRef<HTMLDivElement>(null);

  const renderIcon = () => {
    switch (status) {
      case "IN_PROGRESS":
        return (
          <img
            loading="eager"
            src={icon || downloadGif}
            alt="toast-progress-status-icon"
            data-testid="toast-progress-status-icon"
          />
        );
      case "SUCCESS":
        return (
          <img
            loading="eager"
            src={SuccessGif}
            alt="toast-success-status-icon"
            data-testid="toast-success-status-icon"
          />
        );
      case "ERROR":
        return (
          <img
            src={ErrorGif}
            loading="eager"
            alt="toast-error-status-icon"
            data-testid="toast-error-status-icon"
          />
        );
    }
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  const handleOutsideClick = (event: any) => {
    if (toastRef.current && !toastRef.current.contains(event.target)) {
      handleCloseClick();
    }
  };

  const renderProgressBar = () => {
    if (status !== "IN_PROGRESS") return null;

    return (
      <div className={styles.progressBarContainer}>
        <div
          className={styles.bar}
          style={{ width: `${progress}%` }}
          data-testid="toast-component-progress-bar"
        />
      </div>
    );
  };

  const renderHelperText = () => {
    if (!helperText) return null;

    return (
      <div className={styles.helper}>
        <div className={styles.helperIcon}>
          <CheckIcon />
        </div>
        <span className={styles.helperText}>{helperText}</span>
      </div>
    );
  };

  useEffect(() => {
    if (status) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    // eslint-disable-next-line
  }, [status]);

  const content = (
    <div ref={toastRef} className={styles.toast}>
      <div className={styles.main}>
        <div className={styles.toastIcon}>{renderIcon()}</div>
        <div className={styles.textContainer}>
          <div className={styles.titleContainer}>
            <span className={styles.titleText}>{title}</span>
            <div
              onClick={handleCloseClick}
              className={styles.closeButton}
              data-testid="toast-component-cross-btn"
            >
              <XIcon />
            </div>
          </div>
          <div className={styles.description}>{description}</div>
          {actionButton && (
            <VLButton
              text={actionButton.text}
              onClick={actionButton.onClick}
              type={actionButton.type}
            />
          )}
        </div>
      </div>

      {renderProgressBar()}
      {renderHelperText()}
    </div>
  );

  return createPortal(content, document.body);
};

export default VLToastComponent;
